import React, { useMemo, useRef } from 'react';
import type { FloorPlannerCamera } from '@repo/frontend-types';

export interface CameraPanelHostInput {
  /** The active floor's live cameras. */
  cameras: readonly FloorPlannerCamera[];
  activeFloorIndex: number;
  /** This twin's plan is in the store, so a camera not seen before is a placement. */
  hasPlan: boolean;
  selectedEntityIds: readonly string[];
  /** The plan's pane: where a closed panel's focus falls back to the camera's marker. */
  planPaneRef: { readonly current: HTMLElement | null };
}

export interface CameraPanelHost {
  /** The camera the panel shows, or null. */
  panelRefid: string | null;
  /** The same, readable from a callback without re-creating it. */
  panelRefidRef: { readonly current: string | null };
  /** The camera was placed during this visit and the selection has not left it. */
  panelIsNew: boolean;
  /** Where focus goes when the panel closes, read at close time. */
  panelReturnTo: { readonly current: HTMLElement | null };
}

/**
 * The camera panel's host bookkeeping, shared by Camera Feeds and Floor Plan (`unified-camera-panel`,
 * R1): which camera it shows, whether that camera is new, and where focus goes when it closes. Moved
 * unchanged from `OnboardingCameras` (`camera-feeds-page-panel`).
 */
export const useCameraPanelHost = ({
  cameras,
  activeFloorIndex,
  hasPlan,
  selectedEntityIds,
  planPaneRef,
}: CameraPanelHostInput): CameraPanelHost => {
  /*
   * The camera the panel shows: exactly one selected entity, a live camera on the floor being shown
   * — the condition the panel itself applies (R1). Read here too because the "new camera" header and
   * the focus bookkeeping key on it.
   */
  const panelRefid =
    selectedEntityIds.length === 1 && cameras.some(c => c.refid === selectedEntityIds[0])
      ? selectedEntityIds[0]
      : null;

  /*
   * Which camera is NEW — the mint header (R5): placed during this visit, by a drop or the camera
   * tool, and the selection has not left it since. Worked out during render from what the floor
   * held on the previous render, so the panel opens mint on its first frame rather than flashing
   * butter. A floor switch is not a placement (the other floor's cameras are not "new"), and no
   * baseline is taken until this twin's plan is present — a render before it arrives would otherwise
   * seed an empty floor and call every camera new when the plan lands (review). Idempotent under a
   * repeated render: a second pass adds nothing and deletes nothing it has not already deleted.
   */
  const mintTrack = useRef<{
    floor: number;
    ids: Set<string> | null;
    panel: string | null;
    minted: Set<string>;
  }>({ floor: activeFloorIndex, ids: null, panel: null, minted: new Set() });
  {
    const t = mintTrack.current;
    const ids = hasPlan ? new Set(cameras.map(c => c.refid)) : null;
    if (ids && t.ids && t.floor === activeFloorIndex) {
      ids.forEach(id => {
        if (!t.ids?.has(id)) t.minted.add(id);
      });
    }
    t.floor = activeFloorIndex;
    t.ids = ids;
    if (t.panel !== null && t.panel !== panelRefid) t.minted.delete(t.panel);
    t.panel = panelRefid;
  }
  const panelIsNew = panelRefid !== null && mintTrack.current.minted.has(panelRefid);

  /*
   * Where focus goes when the panel closes (R12): what was focused when it opened or last
   * re-targeted — a tile, a marker — else the camera's marker if it is still on the plan, else the
   * plan's own region. Read at close time, so a camera deleted meanwhile falls through.
   */
  const panelOpenerRef = useRef<HTMLElement | null>(null);
  const panelRefidRef = useRef<string | null>(null);
  panelRefidRef.current = panelRefid;
  /**
   * The camera the panel last showed. NOT cleared on close: the panel reads `panelReturnTo` in its
   * unmount cleanup, after the render that cleared the selection, so the marker fallback needs the
   * camera it was showing, not the null the selection now holds (review).
   */
  const lastPanelRefidRef = useRef<string | null>(null);
  const panelReturnTo = useMemo(
    () => ({
      get current(): HTMLElement | null {
        const opener = panelOpenerRef.current;
        if (opener?.isConnected) return opener;
        const refid = lastPanelRefidRef.current;
        const marker = refid
          ? planPaneRef.current?.querySelector<HTMLElement>(
              `[data-camera-refid="${CSS.escape(refid)}"]`,
            )
          : null;
        // The plan pane by its own marker: the stage holds other regions (the nothing-placed card).
        return (
          marker ?? planPaneRef.current?.querySelector<HTMLElement>('[data-plan-pane]') ?? null
        );
      },
    }),
    // The host's ref object: stable for its life, so this getter is built once.
    [planPaneRef],
  );
  /*
   * Record the opener on open and on every re-target; forget it once the panel has closed (the panel's
   * own unmount cleanup, a child layout cleanup, has read it by then), so a later open whose click
   * focused nothing — Safari, a marker's mousedown — falls to the marker rather than an old tile.
   */
  React.useLayoutEffect(() => {
    if (panelRefid === null) {
      panelOpenerRef.current = null;
      return;
    }
    lastPanelRefidRef.current = panelRefid;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active !== document.body &&
      !active.closest('[data-testid="camera-panel"]')
    ) {
      panelOpenerRef.current = active;
    }
  }, [panelRefid]);

  return { panelRefid, panelRefidRef, panelIsNew, panelReturnTo };
};
